import { useUser, useClerk, useAuth, useSession } from '#imports'
import { ref, computed, watch, onMounted, effectScope, useRuntimeConfig } from '#imports'
import { defineStore } from 'pinia'
import authService from '../utils/authService'
import { musicService } from '~/services/musicService'
import { api } from '~/utils/api'
import { analyticsPath } from '~/utils/analytics-core.mjs'
import { trackEvent, trackConfirmedSignUp } from '~/utils/analytics'
import { useI18n } from 'vue-i18n'
import { useToast } from '~/composables/useToast'
import { getShareAttribution, shareSignUpOptions } from '~/utils/shareAttribution'

// 本地存储键名
const ACCOUNT_CACHE_KEY = 'user_account_cache'
const ACCOUNT_CACHE_OWNER_KEY = 'user_account_cache_owner'
const LOGIN_CONTEXT_KEY = 'musicmint_login_context'
const REFRESH_INTERVAL = 30000
const MIN_REFRESH_INTERVAL = 5000
const BACKEND_ACCOUNT_401_BACKOFF = 2 * 60 * 1000

let globalRefreshTimer: ReturnType<typeof setInterval> | null = null
let lastRefreshTime = 0
let backendAccountUnauthorizedUntil = 0
let localBackendClerkSyncPromise: Promise<boolean> | null = null
let localBackendClerkSyncOwner = ''

const isLocalApiBaseUrl = (baseUrl: string) => {
  if (!baseUrl) return false

  try {
    const parsed = new URL(baseUrl)
    return ['localhost', '127.0.0.1', '::1', '[::1]'].includes(parsed.hostname)
  } catch {
    return false
  }
}

const buildLocalClerkUserCreatedPayload = (clerkUser: any) => {
  if (!clerkUser?.id) return null

  const primaryEmail =
    clerkUser?.primaryEmailAddress?.emailAddress ||
    clerkUser?.emailAddresses?.[0]?.emailAddress ||
    ''

  return {
    type: 'user.created',
    object: 'event',
    timestamp: Date.now(),
    instance_id: 'musicmint-local-dev',
    data: {
      id: clerkUser.id,
      first_name: clerkUser.firstName || '',
      last_name: clerkUser.lastName || '',
      username: clerkUser.username || '',
      email_addresses: primaryEmail
        ? [{ email_address: primaryEmail }]
        : [],
    },
    event_attributes: {
      http_request: {
        client_ip: '127.0.0.1',
        user_agent:
          typeof navigator !== 'undefined'
            ? navigator.userAgent
            : 'MusicMint Local Dev',
      },
    },
  }
}

const buildLoginContext = () => {
  if (typeof window === 'undefined') return null
  return { method: 'clerk', page_path: analyticsPath(window.location.pathname), source: getShareAttribution() ? 'share' : 'direct' }
}

const readStoredLoginContext = () => {
  if (typeof window === 'undefined') return null

  try {
    const raw = localStorage.getItem(LOGIN_CONTEXT_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// 用户账户接口定义
export interface UserAccount {
  id: string
  user_id: string
  type: number
  vip_level: number
  free_points: number
  vip_points: number
  buy_points: number
  has_bought: number
  token: string
  productId: string
  inapp_product_id: string
}

const isValidUserAccount = (value: unknown): value is UserAccount => {
  if (!value || typeof value !== 'object') return false
  const account = value as UserAccount
  return typeof account.id === 'string' && account.id.length > 0 &&
    typeof account.user_id === 'string' && account.user_id.length > 0 &&
    (['type', 'vip_level', 'free_points', 'vip_points', 'buy_points'] as const).every(key =>
      typeof account[key] === 'number' && Number.isFinite(account[key])) &&
    Number.isFinite(account.free_points + account.vip_points + account.buy_points)
}

// 定义 Pinia Store 类型
export interface AccountState {
  userAccount: UserAccount | null
  identityKey: string
  identityRevision: number
  isLoadingAccount: boolean
  isLoggingIn: boolean
  autoFetchingToken: boolean
  isUpdating: boolean
  isInitialized: boolean
  isRefreshing: boolean  // 新增刷新状态
}

// 定义 Pinia Store
export const useAccountStore = defineStore('account', {
  state: (): AccountState => ({
    userAccount: null,
    identityKey: '',
    identityRevision: 0,
    isLoadingAccount: false,
    isLoggingIn: false,
    autoFetchingToken: false,
    isUpdating: false,
    isInitialized: false,
    isRefreshing: false,  // 新增刷新状态
  }),
  
  getters: {
    // 计算总积分
    totalPoints(): number {
      if (!isValidUserAccount(this.userAccount)) return 0
      return this.userAccount.free_points + 
             this.userAccount.vip_points + 
             this.userAccount.buy_points
    }
  },
  
  actions: {
    // A late response must not unlock or populate a newer identity's store.
    bindIdentity(identityKey: string) {
      if (this.identityKey === identityKey) return false
      this.identityKey = identityKey
      ++this.identityRevision
      this.userAccount = null
      this.isLoadingAccount = false
      this.isUpdating = false
      this.isRefreshing = false
      this.autoFetchingToken = false
      return true
    },

    // Keep the legacy account payload intact; ownership is separate cache metadata.
    setUserAccount(account: UserAccount | null, ownerId?: string) {
      if (account && !isValidUserAccount(account)) return
      this.userAccount = account
      // 同步更新缓存
      if (typeof window !== 'undefined') {
        try {
          // Remove ownership first so a partial write cannot bind new data to an old user.
          localStorage.removeItem(ACCOUNT_CACHE_OWNER_KEY)
          if (account) {
            localStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(account))
            if (ownerId) localStorage.setItem(ACCOUNT_CACHE_OWNER_KEY, ownerId)
          } else {
            localStorage.removeItem(ACCOUNT_CACHE_KEY)
          }
        } catch {
          // A blocked cache must not prevent account display or Clerk logout.
        }
      }
    },
    
    // 加载缓存的用户账户 - 仅用于快速显示，不替代API请求
    loadCachedAccount(ownerId: string) {
      if (typeof window === 'undefined' || !ownerId) return false
      try {
        if (localStorage.getItem(ACCOUNT_CACHE_OWNER_KEY) !== ownerId) return false
        const raw = localStorage.getItem(ACCOUNT_CACHE_KEY)
        const cached = raw ? JSON.parse(raw) : null
        if (!isValidUserAccount(cached)) return false
        this.userAccount = cached
        return true
      } catch {
        return false
      }
    },

    // 设置加载状态
    setLoadingAccount(isLoading: boolean) {
      this.isLoadingAccount = isLoading
    },
    
    // 设置登录中状态
    setLoggingIn(isLoggingIn: boolean) {
      this.isLoggingIn = isLoggingIn
    },
    
    // 设置获取token状态
    setAutoFetchingToken(isFetching: boolean) {
      this.autoFetchingToken = isFetching
    },
    
    // 清除用户账户
    clearUserAccount() {
      this.setUserAccount(null)
    },
    
    // 设置初始化状态
    setInitialized(isInitialized: boolean) {
      this.isInitialized = isInitialized
    },
    
    // 设置更新状态
    setUpdating(isUpdating: boolean) {
      this.isUpdating = isUpdating
    },
    
    // 设置刷新状态
    setRefreshing(isRefreshing: boolean) {
      this.isRefreshing = isRefreshing
    },
  }
})

// 全局用户账户状态管理
export function useAuthState() {
  const { isLoaded: userLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const clerk = useClerk();
  const { isLoaded: sessionLoaded, session } = useSession();
  const runtimeConfig = useRuntimeConfig();

  // 使用Pinia Store
  const accountStore = useAccountStore();
  const { t } = useI18n();
  const toast = useToast();

  const isAuthReady = computed(() => userLoaded.value === true && sessionLoaded.value === true);

  // Wait for Clerk user and session state before treating the user as signed in or signed out.
  const isAuthenticated = computed(() => isAuthReady.value && isSignedIn.value === true);

  const getAccountIdentity = () => {
    if (!isAuthenticated.value || !user.value?.id || !session.value?.id) return '';
    return JSON.stringify([user.value.id, session.value.id]);
  };
  const bindCurrentIdentity = () => {
    const identity = getAccountIdentity();
    if (accountStore.bindIdentity(identity)) {
      backendAccountUnauthorizedUntil = 0;
      lastRefreshTime = 0;
      authService.clearToken();
      saveUserAccountToCookie(null);
      stopGlobalRefresh();
    }
    return identity;
  };
  const isCurrentIdentity = (identity: string, revision: number) =>
    identity !== '' && getAccountIdentity() === identity &&
    accountStore.identityKey === identity && accountStore.identityRevision === revision;


  const syncLocalBackendClerkUser = async () => {
    const apiBaseUrl = String(runtimeConfig.public.apiBaseUrl || '')
    if (!isLocalApiBaseUrl(apiBaseUrl)) return false

    const payload = buildLocalClerkUserCreatedPayload(user.value)
    if (!payload) return false

    if (localBackendClerkSyncPromise && localBackendClerkSyncOwner === user.value?.id) {
      return localBackendClerkSyncPromise
    }

    const owner = user.value!.id;
    const request = Promise.resolve().then(async () => {
      try {
        const response = await api.post<any>('music/public/v1/clerk/webhook', payload, {
          timeout: 10000,
        })
        const ok = response?.code === 200
        if (ok) {
          console.log('syncLocalBackendClerkUser: Synced Clerk user to local backend.')
        } else {
          console.warn(
            'syncLocalBackendClerkUser: Local backend sync returned unexpected response.',
            response
          )
        }
        return ok
      } catch (error) {
        console.warn(
          'syncLocalBackendClerkUser: Failed to sync Clerk user to local backend.',
          error
        )
        return false
      }
    }).finally(() => {
      if (localBackendClerkSyncPromise === request) {
        localBackendClerkSyncPromise = null
        localBackendClerkSyncOwner = ''
      }
    })
    localBackendClerkSyncPromise = request
    localBackendClerkSyncOwner = owner
    return request
  }

  // 处理登录
  const handleSignIn = async () => {
    if (accountStore.isLoggingIn) return false;
    try {
      // 设置登录中状态
      accountStore.setLoggingIn(true);
      // Auth resources can arrive before Clerk's UI is ready. Its public
      // status event also works when the shallow provider ref does not change.
      if (!isAuthReady.value || clerk.value?.status !== 'ready') {
        let stop: (() => void) | undefined;
        let timeout: ReturnType<typeof setTimeout> | undefined;
        try {
          await new Promise<void>((resolve, reject) => {
            timeout = setTimeout(() => reject(new Error('Login service unavailable')), 20000);
            stop = watch([isAuthReady, clerk], ([authReady, instance], _previous, onCleanup) => {
              if (!instance) return;
              const onStatus = () => {
                if (authReady && instance.status === 'ready') resolve();
              };
              onCleanup(() => instance.off('status', onStatus));
              instance.on('status', onStatus, { notify: true });
              onStatus();
            }, { immediate: true });
          });
        } finally {
          clearTimeout(timeout);
          stop?.();
        }
      }
      if (!clerk.value) throw new Error('Login service unavailable');
      const loginContext = buildLoginContext();
      if (loginContext && typeof window !== 'undefined') {
        try { localStorage.setItem(LOGIN_CONTEXT_KEY, JSON.stringify(loginContext)); } catch { /* Optional metrics never block login. */ }
        trackEvent('login_start', loginContext);
      }
      // console.log('window.location.href',window.location)
      // 使用clerk实例直接调用
      // Keep sign-in as the entry for returning users; Clerk copies
      // unsafeMetadata onto the user when the flow transfers to sign-up.
      await clerk.value.openSignIn(getShareAttribution()
        ? shareSignUpOptions({
          fallbackRedirectUrl: window.location.href,
          unsafeMetadata: { ...clerk.value.client?.signUp?.unsafeMetadata },
        })
        : { fallbackRedirectUrl: window.location.href });

      // 监听登录状态变化，当isSignedIn发生变化时表示登录成功
      // signInInstance is void, watch isSignedIn for login success
      const unwatchSignIn = watch([isSignedIn, () => user.value?.id, () => session.value?.id], ([signedIn, userId, sessionId]) => {
        if (signedIn === true && userId && sessionId) {
          const storedLoginContext = readStoredLoginContext();
          if (storedLoginContext && typeof window !== 'undefined') {
            trackEvent('login_success', {
              ...storedLoginContext,
              page_path_after_login: `${window.location.pathname}${window.location.search}`,
            });
            try { localStorage.removeItem(LOGIN_CONTEXT_KEY); } catch { /* Optional metrics only. */ }
          }
          accountStore.setLoggingIn(false);
          unwatchSignIn(); // 清理监听
          // 登录成功后，initAuthStateWatcher 中的 isAuthenticated watch 会处理后续
          // fetchUserAccount(); // Redundant if watcher handles it
        }
      });

      // Start this reset only after Clerk is ready and openSignIn has run.
      // 简单的超时机制，避免状态卡住
      // 如果用户关闭弹窗但未登录，或者登录过程被中断，会自动重置状态
      setTimeout(() => {
        if (accountStore.isLoggingIn) {
          accountStore.setLoggingIn(false);
          unwatchSignIn(); // 清理监听
        }
      }, 5000); // Increased timeout slightly // 设置合理的超时时间

      return true;
    } catch (error) {
      console.error("Sign in failed:", error);
      toast.error(t('ux.loginUnavailable'));
      accountStore.setLoggingIn(false);
      return false;
    }
  };

  // Token results belong to the Clerk user and session that requested them.
  const fetchAndStoreToken = async () => {
    const identity = bindCurrentIdentity();
    const revision = accountStore.identityRevision;
    if (!identity) return null;
    accountStore.setAutoFetchingToken(true);
    try {
      const jwt = getToken.value ? await getToken.value({ template: 'supabase' }) : null;
      if (!isCurrentIdentity(identity, revision)) return null;
      if (jwt) {
        authService.saveToken(jwt);
        return jwt;
      }
      authService.clearToken();
      return null;
    } catch {
      if (isCurrentIdentity(identity, revision)) authService.clearToken();
      return null;
    } finally {
      if (isCurrentIdentity(identity, revision)) accountStore.setAutoFetchingToken(false);
    }
  };

  // 清除token
  const clearToken = () => {
    authService.clearToken();
  };

  // 处理登出
  const handleLogout = async () => {
    try {
      accountStore.bindIdentity('');
      clearToken();
      accountStore.clearUserAccount();
      saveUserAccountToCookie(null);
      stopGlobalRefresh(); // Stop refresh timers first
      await clerk.value?.signOut(); // This should trigger isSignedIn watcher
      console.log("Logout process completed.");
      return true;
    } catch (error) {
      console.error("登出失败:", error);
      const identity = bindCurrentIdentity();
      const revision = accountStore.identityRevision;
      if (identity) fetchUserAccount().then(() => {
        if (isCurrentIdentity(identity, revision)) startGlobalRefresh();
      });
      return false;
    }
  };

  // Account responses, cache writes and loading flags are scoped to one identity generation.
  const fetchUserAccount = async () => {
    const identity = bindCurrentIdentity();
    const revision = accountStore.identityRevision;
    const owner = user.value?.id;
    if (!identity) {
      if (isAuthReady.value && !isSignedIn.value) {
        backendAccountUnauthorizedUntil = 0;
        accountStore.clearUserAccount();
        clearToken();
        saveUserAccountToCookie(null);
        stopGlobalRefresh();
      }
      return null;
    }
    if (backendAccountUnauthorizedUntil > Date.now() || accountStore.isUpdating) return accountStore.userAccount;
    accountStore.setLoadingAccount(true);
    accountStore.setUpdating(true);
    accountStore.setRefreshing(true);
    const commitAccount = (account: UserAccount) => {
      backendAccountUnauthorizedUntil = 0;
      accountStore.setUserAccount(account, owner);
      saveUserAccountToCookie(account);
      return account;
    };
    try {
      const token = await fetchAndStoreToken();
      if (!isCurrentIdentity(identity, revision) || !token) return null;
      const response = await musicService.getUserAccount();
      if (!isCurrentIdentity(identity, revision)) return null;
      if (response?.code === 401) {
        const synced = await syncLocalBackendClerkUser();
        if (!isCurrentIdentity(identity, revision)) return null;
        if (synced) {
          const retry = await musicService.getUserAccount();
          if (!isCurrentIdentity(identity, revision)) return null;
          if (retry?.code === 200 && isValidUserAccount(retry.data)) return commitAccount(retry.data);
        }
        backendAccountUnauthorizedUntil = Date.now() + BACKEND_ACCOUNT_401_BACKOFF;
        accountStore.clearUserAccount();
        saveUserAccountToCookie(null);
        return null;
      }
      if (response?.code === 200 && isValidUserAccount(response.data)) return commitAccount(response.data);
      if (response?.code === 200 && response.data === null) {
        backendAccountUnauthorizedUntil = 0;
        accountStore.clearUserAccount();
        saveUserAccountToCookie(null);
        return null;
      }
      // A transient or malformed response must not replace verified same-identity data.
      return accountStore.userAccount;
    } catch {
      return isCurrentIdentity(identity, revision) ? accountStore.userAccount : null;
    } finally {
      if (isCurrentIdentity(identity, revision)) {
        accountStore.setLoadingAccount(false);
        accountStore.setUpdating(false);
        accountStore.setRefreshing(false);
      }
    }
  };

  // 等待token并获取账户信息的公共函数
  const waitForTokenAndFetchAccount = (source = "未知") => {
    console.log(`waitForTokenAndFetchAccount called from: ${source}`);
    if (isAuthenticated.value) {
        fetchUserAccount();
    } else {
        // If not signed in, no point waiting for token. Watcher will handle sign-in.
        console.log("waitForTokenAndFetchAccount: Not signed in, will not wait for token.");
    }
  };

  // 全局账户信息刷新函数
  const refreshUserAccount = async (force = false) => {
    const identity = bindCurrentIdentity();
    const revision = accountStore.identityRevision;
    if (!identity) return;
    const now = Date.now();

    // 如果不是强制刷新，检查是否需要刷新
    if (!force && now - lastRefreshTime < MIN_REFRESH_INTERVAL) {
      return;
    }

    // 如果正在更新中，跳过
    if (accountStore.isUpdating && !force) {
      console.log("refreshUserAccount: Skipped, update in progress and not forced.");
      return;
    }
    // fetchUserAccount will check isSignedIn and manage token itself.
    if(isAuthenticated.value) {
        try {
            await fetchUserAccount();
            if (isCurrentIdentity(identity, revision)) lastRefreshTime = now;
        } catch (error) {
            console.error("refreshUserAccount: Error during fetchUserAccount:", error);
        }
    } else {
        // console.log("refreshUserAccount: Skipped, user not signed in (Clerk).")
    }
  };

  // 启动全局定时刷新
  const startGlobalRefresh = () => {
    if (globalRefreshTimer) return;
    console.log("Starting global refresh timer.");
    globalRefreshTimer = setInterval(() => {
      if (isAuthenticated.value) { // isAuthenticated is now isSignedIn.value
        refreshUserAccount();
      }
    }, REFRESH_INTERVAL);
  };

  // 停止全局定时刷新
  const stopGlobalRefresh = () => {
    if (globalRefreshTimer) {
      console.log("Stopping global refresh timer.");
      clearInterval(globalRefreshTimer);
      globalRefreshTimer = null;
    }
  };

  // 保存用户账户信息到cookie，7天过期，用于插件获取用户信息
  const saveUserAccountToCookie = (account: UserAccount | null) => {
    if (account) {
      document.cookie = `user_account=${JSON.stringify(
        account
      )}; path=/; secure; samesite=strict; max-age=604800`; // 7天过期
    } else {
      // 清除 cookie
      document.cookie =
        "user_account=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    }
  };

  // Observe the identity itself: switching authenticated users need not change isSignedIn.
  const initAuthStateWatcher = () => {
    if (accountStore.isInitialized) return;
    // The shared account watcher outlives the first navigation widget that mounts it.
    effectScope(true).run(() => watch([isAuthReady, isAuthenticated, () => user.value?.id, () => session.value?.id], () => {
      const previousIdentity = accountStore.identityKey;
      const identity = bindCurrentIdentity();
      const revision = accountStore.identityRevision;
      if (!isAuthReady.value) return;
      if (identity) {
        trackConfirmedSignUp(user.value!.id, session.value!.id, clerk.value?.client?.signUp);
        if (previousIdentity !== identity) accountStore.loadCachedAccount(user.value!.id);
        fetchUserAccount().then(() => {
          if (isCurrentIdentity(identity, revision)) startGlobalRefresh();
        });
      } else {
        clearToken();
        accountStore.clearUserAccount();
        saveUserAccountToCookie(null);
        stopGlobalRefresh();
      }
    }, { immediate: true }));
    accountStore.setInitialized(true);
  };

  // 获取临时用户id
  const getTempUserId = () => {
    const tempUserId = localStorage.getItem("temp_user_id");
    console.log("tempUserId", tempUserId);
    if (tempUserId) {
      return tempUserId;
    }
    return null;
  };

  // 设置临时用户id
  const setTempUserId = (userId: string) => {
    localStorage.setItem("temp_user_id", userId);
  };

  // 修改 onMounted 逻辑
  onMounted(() => {
    console.log("客户端挂载完成，检查初始化状态");

    // 只在客户端且未初始化时执行初始化逻辑
    if (typeof window !== 'undefined' && !accountStore.isInitialized) {
      console.log("onMounted: Client mounted, initializing auth state watcher.");
      initAuthStateWatcher();
    }
  });

  // Hide the previous identity immediately, before the async watcher has run.
  const currentUserAccount = computed(() => {
    const identity = getAccountIdentity();
    return identity && identity === accountStore.identityKey ? accountStore.userAccount : null;
  });

  // 从store获取响应式状态，确保所有组件共享相同的状态
  return {
    isAuthenticated,
    isAuthReady,
    isLoggingIn: computed(() => accountStore.isLoggingIn),
    handleSignIn,
    fetchAndStoreToken, // Expose if needed externally, but primarily used internally
    handleLogout,
    user, // from useUser()
    userAccount: currentUserAccount,
    isLoadingAccount: computed(() => accountStore.isLoadingAccount),
    isRefreshing: computed(() => accountStore.isRefreshing), // 新增
    totalPoints: computed(() => currentUserAccount.value ? accountStore.totalPoints : 0),
    refreshUserAccount, // 导出刷新方法
    waitForTokenAndFetchAccount, // Review if this is still needed with new flow
    getTempUserId,
    setTempUserId,
  };
} 
